import { makeAutoObservable, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import { logger } from 'shared/lib'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { toastStore } from 'shared/ui'
import { InboxesApi } from 'entities/Inbox'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type IParamsTeamsGroupsUpdate } from 'entities/Inbox/api/types'
import { type GroupInbox } from 'entities/Inbox/model/GroupInbox'
import { SendFromMultiStore } from 'widgets/SendFromMulti/store/SendFromMultiStore'
import { type InboxesStore } from 'pages/settings/pages/inboxes/store/inboxesStore'
import { GroupInboxModalContent } from 'pages/settings/pages/inboxes/pages/inboxesView/ui/GroupInboxModal/GroupInboxModalContent'
import { GroupInboxModalAction } from 'pages/settings/pages/inboxes/pages/inboxesView/ui/GroupInboxModal/GroupInboxModalAction'
import { type GroupInboxesViewStore } from './groupInboxesViewStore'

export class GroupInboxFormStore {
  private _groupInboxModalLoading = false
  private _modalId = nanoid()
  private _groupInboxName = ''
  private _groupInboxDescription = ''
  private _groupInboxSelectedInboxIds: number[] = []
  private _groupInboxId: number | null = null
  private _sendFromMultiStore: SendFromMultiStore | null = null
  private _errorsMap: Map<string, string> = new Map()

  constructor(
    private _groupInboxesViewStore: GroupInboxesViewStore,
    private _inboxesStore: InboxesStore,
    private _entities: IEntitiesStore
  ) {
    makeAutoObservable(this)
  }

  get groupInboxModalLoading() {
    return this._groupInboxModalLoading
  }

  get groupInboxName() {
    return this._groupInboxName
  }

  get groupInboxDescription() {
    return this._groupInboxDescription
  }

  get groupInboxSelectedInboxIds() {
    return this._groupInboxSelectedInboxIds
  }

  get sendFromMultiStore() {
    return this._sendFromMultiStore
  }

  getFormError = (field: string) => {
    return this._errorsMap.get(field)
  }

  get availableInboxes() {
    const allInboxes = this._inboxesStore.inboxesViewStore.items
    const usedInboxIds = new Set<number>()

    const groupInboxes = this._groupInboxesViewStore.items
    groupInboxes.forEach((groupInbox) => {
      if (this._groupInboxId && groupInbox.id === this._groupInboxId) {
        return
      }
      groupInbox.teamsIds.forEach((teamId: number) => {
        usedInboxIds.add(teamId)
      })
    })

    return allInboxes.filter((inbox) => !usedInboxIds.has(inbox.id))
  }

  setGroupInboxName = (name: string) => {
    this._groupInboxName = name
    this.removeError('name')
  }

  setGroupInboxDescription = (description: string) => {
    this._groupInboxDescription = description
  }

  setGroupInboxSelectedInboxIds = (ids: number[]) => {
    this._groupInboxSelectedInboxIds = ids
    this.removeError('inboxes')
  }

  setError = (key: string, error: string) => {
    this._errorsMap.set(key, error)
  }

  removeError = (key: string) => {
    this._errorsMap.delete(key)
  }

  clearAllFormErrors = () => {
    this._errorsMap.clear()
  }

  resetGroupInboxForm = () => {
    this._groupInboxName = ''
    this._groupInboxDescription = ''
    this._groupInboxSelectedInboxIds = []
    this._groupInboxId = null
    this.clearAllFormErrors()
  }

  createSendFromMultiStore = async () => {
    this._sendFromMultiStore = new SendFromMultiStore(this._entities, {
      getDropdownItemsOptions: {
        excludeShortCode: true,
        hideChildren: true,
        disabledClosedTooltip: true,
      },
      preloadedInboxes: this.availableInboxes.map((inbox) => inbox.origin),
      isInboxMode: true,
    })

    if (this._groupInboxSelectedInboxIds.length > 0) {
      await this._sendFromMultiStore?.setInboxIds(Array.from(this._groupInboxSelectedInboxIds))
    }

    this._sendFromMultiStore?.setOnSelectionChange((selectedIds: number[]) => {
      this.setGroupInboxSelectedInboxIds(selectedIds)
    })
  }

  validateForm = () => {
    let isValid = true
    if (this.groupInboxName.trim().length < 3) {
      isValid = false
      this.setError('name', 'Group name must be a minimum of 3 characters')
    }

    if (this.groupInboxSelectedInboxIds.length === 0) {
      isValid = false
      this.setError('inboxes', 'Please select at least one inbox')
    }

    return isValid
  }

  createGroupInbox = async (isEdit?: boolean) => {
    const isValid = this.validateForm()
    if (!isValid) return

    runInAction(() => {
      this._groupInboxModalLoading = true
    })
    try {
      const params: IParamsTeamsGroupsUpdate = {
        name: this.groupInboxName,
        description: this.groupInboxDescription,
        team_ids: !isEdit ? this.groupInboxSelectedInboxIds : undefined,
        teamIds: isEdit ? this.groupInboxSelectedInboxIds : undefined,
      }

      const { data } =
        isEdit && this._groupInboxId
          ? await InboxesApi.updateTeamsGroupsById(this._groupInboxId, params)
          : await InboxesApi.updateTeamsGroupsCreate(params)

      this._groupInboxesViewStore.setItem(data)
      this.closeModal()

      toastStore.add({
        title: isEdit ? 'Group inbox updated' : 'Group inbox created',
        type: 'info',
      })
    } catch (error) {
      logger.error(error)
    } finally {
      runInAction(() => {
        this._groupInboxModalLoading = false
      })
    }
  }

  closeModal = () => {
    this.resetGroupInboxForm()
    modalStore.removeModal(this._modalId)
  }

  handleGroupInboxModal = async (isEdit?: boolean) => {
    await this.createSendFromMultiStore()

    modalStore.addModal({
      id: this._modalId,
      title: isEdit ? 'Edit group inbox' : 'Create group inbox',
      ModalContent: () => <GroupInboxModalContent store={this} />,
      width: 480,
      ModalActions: () => <GroupInboxModalAction store={this} isEdit={isEdit} />,
      onClose: () => this.closeModal(),
    })
  }

  handleEditGroupInbox = async (groupInbox: GroupInbox) => {
    this._groupInboxId = groupInbox.id
    this.setGroupInboxName(groupInbox.name)
    this.setGroupInboxDescription(groupInbox.description)
    this.setGroupInboxSelectedInboxIds(groupInbox.teamsIds)
    this.handleGroupInboxModal(true)
  }
}
